import { useTranslation } from '@pancakeswap/localization'
import { ChainDefaultId } from '@pancakeswap/sdk'
import { Box, Flex, FlexGap, LinkExternal, OpenNewIcon, Text } from '@pancakeswap/uikit'
import Link from 'next/link'
import { TransactionType } from 'state/transactions/actions'
import { TransactionDetails } from 'state/transactions/reducer'
import { styled } from 'styled-components'
import { getBlockExploreLink } from 'utils'
import { getTokenLogoURLWithSymbol } from 'utils/getTokenLogoURL'

interface TransactionRowProps {
  txn: TransactionDetails
  chainId?: number
  type?: TransactionType
  onDismiss?: () => void
  asToast?: boolean
}

const Card = styled.div`
  border-radius: 12px;
  padding: 12px;
  width: 100%;
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

const Title = styled.h2`
  font-size: 14px;
  font-weight: bold;
  margin: 0;
  flex: 1;
  text-transform: capitalize;
  
  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 16px;
  }
`;

const SwapDetails = styled.div`
  font-size: 12px;
  margin-bottom: 4px;
  word-break: break-word;
  
  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 14px;
  }
`;

const Timestamp = styled.div`
  color: ${({ theme }) => theme.colors.textSubtle};
  font-size: 10px;
  
  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 12px;
  }
`;

const CurrencyIcon = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 50%;

  &:not(:first-child) {
    margin-left: -8px;
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    width: 20px;
    height: 20px;
  }
`;

const StyledLinkExternal = styled(LinkExternal)`
  font-size: 10px;
  
  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 12px;
  }
`;

const RowContainer = styled(Flex) <{ asToast: boolean }>`
  background: var(--Gray-800, #2B2634);
  ${({ asToast }) => asToast ? `
    background: transparent;
  ` : ''}
  border-radius: 8px;
  padding: 12px;
  align-items: flex-start;
  gap: 12px;
`

const ContentContainer = styled(Flex)`
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

const Row = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const WaitIcon = () => (
  <Box bg="#FFF1" borderRadius={9} p="12px">
    <svg width="32" height="32" viewBox="0 0 20 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.5 28.3332H18.5M1.99995 1.6665V6.99984C1.99995 11.4181 5.58168 14.9998 9.99996 14.9998M18 28.3332V22.9998C18 18.5816 14.4182 14.9998 9.99996 14.9998M9.99996 14.9998C5.58168 14.9998 1.99995 18.5816 1.99995 22.9998V28.3332M9.99996 14.9998C14.4182 14.9998 18 11.4181 18 6.99984V1.6665M14 6.99984C14 9.20898 12.2091 10.9998 9.99996 10.9998C7.79082 10.9998 5.99995 9.20898 5.99995 6.99984M5.99995 24.3332V24.3332C8.39646 22.6358 11.6035 22.6357 14 24.3332V24.3332M1.50012 1.6665H18.5001" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  </Box>
)

const StatusIcon = ({ status }: { status: 'success' | 'pending' | 'failed' }) => {
  if (status === 'success') {
    return (
      <Box bg="#2EBF431A" borderRadius={9} p="12px">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 4C16.5523 4 17 3.55228 17 3C17 2.44772 16.5523 2 16 2V4ZM22 12C22 11.4477 21.5523 11 21 11C20.4477 11 20 11.4477 20 12H22ZM5.18404 20.564L5.63803 19.673H5.63803L5.18404 20.564ZM3.43597 18.816L4.32698 18.362L4.32698 18.362L3.43597 18.816ZM18.816 20.564L18.362 19.673H18.362L18.816 20.564ZM20.564 18.816L19.673 18.362V18.362L20.564 18.816ZM3.43597 5.18404L2.54497 4.73005L2.54497 4.73005L3.43597 5.18404ZM5.18404 3.43597L4.73005 2.54497L4.73005 2.54497L5.18404 3.43597ZM12.4173 13.6427L11.7077 12.9381L11.7077 12.9381L12.4173 13.6427ZM21.7096 5.70464C22.0987 5.31276 22.0965 4.67959 21.7046 4.29043C21.3128 3.90127 20.6796 3.90348 20.2904 4.29536L21.7096 5.70464ZM11.7077 13.6427L12.4173 12.9381L12.4173 12.9381L11.7077 13.6427ZM8.70957 9.20445C8.32041 8.81257 7.68724 8.81036 7.29536 9.19952C6.90348 9.58869 6.90127 10.2218 7.29043 10.6137L8.70957 9.20445ZM14.6 20H9.4V22H14.6V20ZM4 14.6V9.4H2V14.6H4ZM9.4 4H16V2H9.4V4ZM20 12V14.6H22V12H20ZM9.4 20C8.26339 20 7.47108 19.9992 6.85424 19.9488C6.24907 19.8994 5.90138 19.8072 5.63803 19.673L4.73005 21.455C5.32234 21.7568 5.96253 21.8826 6.69138 21.9422C7.40855 22.0008 8.2964 22 9.4 22V20ZM2 14.6C2 15.7036 1.99922 16.5914 2.05782 17.3086C2.11737 18.0375 2.24318 18.6777 2.54497 19.27L4.32698 18.362C4.19279 18.0986 4.10062 17.7509 4.05118 17.1458C4.00078 16.5289 4 15.7366 4 14.6H2ZM5.63803 19.673C5.07354 19.3854 4.6146 18.9265 4.32698 18.362L2.54497 19.27C3.02433 20.2108 3.78924 20.9757 4.73005 21.455L5.63803 19.673ZM14.6 22C15.7036 22 16.5914 22.0008 17.3086 21.9422C18.0375 21.8826 18.6777 21.7568 19.27 21.455L18.362 19.673C18.0986 19.8072 17.7509 19.8994 17.1458 19.9488C16.5289 19.9992 15.7366 20 14.6 20V22ZM20 14.6C20 15.7366 19.9992 16.5289 19.9488 17.1458C19.8994 17.7509 19.8072 18.0986 19.673 18.362L21.455 19.27C21.7568 18.6777 21.8826 18.0375 21.9422 17.3086C22.0008 16.5914 22 15.7036 22 14.6H20ZM19.27 21.455C20.2108 20.9757 20.9757 20.2108 21.455 19.27L19.673 18.362C19.3854 18.9265 18.9265 19.3854 18.362 19.673L19.27 21.455ZM4 9.4C4 8.26339 4.00078 7.47108 4.05118 6.85424C4.10062 6.24907 4.19279 5.90138 4.32698 5.63803L2.54497 4.73005C2.24318 5.32234 2.11737 5.96253 2.05782 6.69138C1.99922 7.40855 2 8.2964 2 9.4H4ZM9.4 2C8.2964 2 7.40855 1.99922 6.69138 2.05782C5.96253 2.11737 5.32234 2.24318 4.73005 2.54497L5.63803 4.32698C5.90138 4.19279 6.24907 4.10062 6.85424 4.05118C7.47108 4.00078 8.26339 4 9.4 4V2ZM4.32698 5.63803C4.6146 5.07354 5.07354 4.6146 5.63803 4.32698L4.73005 2.54497C3.78924 3.02433 3.02433 3.78924 2.54497 4.73005L4.32698 5.63803ZM13.1268 14.3474L21.7096 5.70464L20.2904 4.29536L11.7077 12.9381L13.1268 14.3474ZM12.4173 12.9381L8.70957 9.20445L7.29043 10.6137L10.9982 14.3474L12.4173 12.9381ZM11.7077 12.9381C11.9033 12.7412 12.2217 12.7412 12.4173 12.9381L10.9981 14.3474C11.5848 14.9381 12.5402 14.9381 13.1269 14.3474L11.7077 12.9381Z" fill="#2EBF43" />
        </svg>
      </Box>
    )
  }
  if (status === 'failed') {
    return (
      <Box bg="#FB68601A" borderRadius={9} p="12px">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 15L12 12M12 12L9 9M12 12L9 15M12 12L15 9M1 12C1 18.0751 5.92487 23 12 23C18.0751 23 23 18.0751 23 12C23 5.92487 18.0751 1 12 1C5.92487 1 1 5.92487 1 12Z" stroke="#E33B32" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </Box>
    )
  }
  return (
    <Box bg="#EEA23E1A" borderRadius={9} p="12px">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 8V14M12 17V17.01M23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12Z" stroke="#EEA23E" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </Box>
  )
}

const renderIcon = (txn: TransactionDetails) => {
  if (!txn.receipt) {
    return <StatusIcon status="pending" />;
  }

  return txn.receipt.status === 'success' ? (
    <StatusIcon status="success" />
  ) : (
    <StatusIcon status="failed" />
  );
}

const getTokenSymbol = (address: string, tokenSymbol: string) => {
  let symbol = tokenSymbol
  if (address?.toLowerCase() === "0xdAC17F958D2ee523a2206206994597C13D831ec7".toLowerCase()) { // pUSDT
    symbol = 'pusdt'
  }
  if (address?.toLowerCase() === "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48".toLowerCase()) { // pUSDC
    symbol = 'pusdc'
  }
  if (address?.toLowerCase() === "0x6B175474E89094C44Da98b954EedeAC495271d0F".toLowerCase()) { // pDAI
    symbol = 'pdai'
  }
  return symbol
}

const TransactionRow: React.FC<React.PropsWithChildren<TransactionRowProps>> = ({ txn, chainId = ChainDefaultId, asToast }) => {
  const { t } = useTranslation()
  // const dispatch = useAppDispatch()

  const url = txn.hash ? getBlockExploreLink(txn.hash, 'transaction', chainId) : undefined

  if (!txn) {
    return null
  }

  const timestamp = txn.confirmedTime ? new Date(txn.confirmedTime).toLocaleString() : ""
  const tokenLogos = []
  let txTitle = String(txn.type).split("-").map(w => `${w[0].toUpperCase()}${w.slice(1)}`).join(' ')
  // const explorerLink = getBlockExploreLink(txn.hash, 'transaction', chainId)

  if (txn.type === 'swap' || txn.type === 'dca-submission') {
    if (txn.type === 'dca-submission')
      txTitle = "DCA"
    else
      txTitle = "Swap"

    let token0Symbol = txn.translatableSummary?.data?.inputSymbol.toString().toLowerCase()
    let token1Symbol = txn.translatableSummary?.data?.outputSymbol.toString().toLowerCase()
    token0Symbol = getTokenSymbol(txn.translatableSummary?.data?.token0Address?.toString(), token0Symbol)
    token1Symbol = getTokenSymbol(txn.translatableSummary?.data?.token1Address?.toString(), token1Symbol)

    console.log("token0Symbol", token0Symbol)
    console.log("token1Symbol", token1Symbol)

    tokenLogos.push(
      getTokenLogoURLWithSymbol(token0Symbol),
      getTokenLogoURLWithSymbol(token1Symbol)
    )
  } else if (txn.type.endsWith('-liquidity')) {
    if (txn.type === 'add-liquidity')
      txTitle = "Add Liquidity"
    else if (txn.type === 'remove-liquidity')
      txTitle = "Remove Liquidity"

    let token0Symbol = txn.translatableSummary?.data?.symbolA.toString().toLowerCase()
    let token1Symbol = txn.translatableSummary?.data?.symbolB.toString().toLowerCase()
    token0Symbol = getTokenSymbol(txn.translatableSummary?.data?.token0Address?.toString(), token0Symbol)
    token1Symbol = getTokenSymbol(txn.translatableSummary?.data?.token1Address?.toString(), token1Symbol)

    tokenLogos.push(
      getTokenLogoURLWithSymbol(token0Symbol),
      getTokenLogoURLWithSymbol(token1Symbol)
    )
  } else if (txn.type.endsWith('-liquidity-v3') || txn.type === 'stake-v3' || txn.type === 'unstake-v3') {
    if (txn.type === 'add-liquidity-v3')
      txTitle = "Add Liquidity V3"
    else if (txn.type === 'increase-liquidity-v3')
      txTitle = "Increase Liquidity V3"
    else if (txn.type === 'remove-liquidity-v3')
      txTitle = "Remove Liquidity V3"
    else if (txn.type === 'stake-v3')
      txTitle = "Deposit"
    else if (txn.type === 'unstake-v3')
      txTitle = "Withdraw"

    let token0Symbol = txn.translatableSummary?.data?.baseSymbol.toString().toLowerCase()
    let token1Symbol = txn.translatableSummary?.data?.quoteSymbol.toString().toLowerCase()
    token0Symbol = getTokenSymbol(txn.translatableSummary?.data?.token0Address?.toString(), token0Symbol)
    token1Symbol = getTokenSymbol(txn.translatableSummary?.data?.token1Address?.toString(), token1Symbol)
    tokenLogos.push(
      getTokenLogoURLWithSymbol(token0Symbol),
      getTokenLogoURLWithSymbol(token1Symbol)
    )
  } else if (txn.type === 'collect-fee') {

    let token0Symbol = txn.translatableSummary?.data?.symbol0.toString().toLowerCase()
    let token1Symbol = txn.translatableSummary?.data?.symbol1.toString().toLowerCase()
    token0Symbol = getTokenSymbol(txn.translatableSummary?.data?.token0Address?.toString(), token0Symbol)
    token1Symbol = getTokenSymbol(txn.translatableSummary?.data?.token1Address?.toString(), token1Symbol)

    tokenLogos.push(
      getTokenLogoURLWithSymbol(token0Symbol),
      getTokenLogoURLWithSymbol(token1Symbol)
    )
  } else if (txn.type === 'limit-order-submission') {
    txTitle = "Open Order"
  } else if (txn.type === 'limit-order-cancellation') {
    txTitle = "Cancel Order"
  } else if (txn.type === 'limit-order-approval') {
    txTitle = "Approve Order"
  }

  // const getStatusIcon = (status) => {
  //   switch(status) {
  //     case 'success': return '✓'
  //     case 'pending': return '⚠️'
  //     case 'failed': return '✕'
  //     default: return '✓'
  //   }
  // }

  return (
    <RowContainer asToast={asToast}>
      {!txn.receipt && asToast ? <WaitIcon /> : renderIcon(txn)}
      <ContentContainer>
        <Row>
          <FlexGap alignItems="center" gap="4px">
            {(tokenLogos.filter(Boolean).length > 0) && (
              <Flex>
                {tokenLogos.filter(Boolean).map((logo, index) => <CurrencyIcon key={`logo-${index}-${logo}`} src={logo} alt="" />)}
              </Flex>
            )}
            <Text color="white">{txTitle}</Text>
          </FlexGap>
          {
            !asToast && !!url &&
            <Link href={url} target='_blank'>
              <OpenNewIcon color="#777" width={16} />
            </Link>
          }
        </Row>
        <Row>
          <Text fontSize="12px" color="#A39CB1">
            {txn.translatableSummary
              ? t(txn.translatableSummary.text, txn.translatableSummary.data)
              : txn.summary ?? txn.hash}
          </Text>
          {
            !asToast &&
            <Text fontSize="12px" color="#A39CB1">
              {timestamp}
            </Text>
          }
        </Row>
        {
          asToast && !!url &&
          <FlexGap gap="8px">
            <Text fontSize="12px" color="#A39CB1">{t('View transaction details')}</Text>
            <Link href={url} target='_blank'>
              <OpenNewIcon color="#777" width={16} />
            </Link>
          </FlexGap>
        }
      </ContentContainer>
    </RowContainer>
  )
}

export default TransactionRow